import React, { useCallback, useState } from 'react';
import { Calendar, Heart, Save, RefreshCw, Calculator, Book } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import CycleParametersSection from './CycleParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import SymptomsSection from './SymptomsSection';

// Импорт утилитарных функций
import { isValidDate } from '../../../../../utils/validation';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета овуляции
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setCalendarData,
  cycleTypes,
  ovulationSymptoms,
  saveCycle,
  calculateFertilityWindow,
  generateCalendarData 
}) => {
  
  // Состояние для имени сохраняемого цикла
  const [saveCycleName, setSaveCycleName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeSymptoms' && !checked) {
        newState.includeBbt = false;
        newState.includeCervicalMucus = false;
        newState.ovulationTestDate = '';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      lastPeriodDate, cycleLength, periodLength, lutealPhaseLength,
      includeSymptoms, includeBbt, bbtRise, includeCervicalMucus,
      ovulationTestDate
    } = formData;

    // Парсинг числовых значений
    const numCycleLength = parseInt(cycleLength, 10);
    const numPeriodLength = parseInt(periodLength, 10);
    const numLutealPhaseLength = parseInt(lutealPhaseLength, 10);
    const numBbtRise = parseFloat(bbtRise);

    // Валидация базовых полей
    if (!lastPeriodDate) {
      newErrors.lastPeriodDate = 'Необходимо указать дату';
    } else if (!isValidDate(lastPeriodDate)) {
      newErrors.lastPeriodDate = 'Некорректная дата';
    } else {
      const lastPeriodDateObj = new Date(lastPeriodDate);
      const currentDate = new Date();
      
      // Проверяем, что дата не в будущем
      if (lastPeriodDateObj > currentDate) {
        newErrors.lastPeriodDate = 'Дата не может быть в будущем';
      }
      
      // Проверяем, что дата не слишком в прошлом (не более 1 года назад)
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      if (lastPeriodDateObj < oneYearAgo) {
        newErrors.lastPeriodDate = 'Дата не может быть более 1 года назад';
      }
    }

    if (!cycleLength) {
      newErrors.cycleLength = 'Необходимо указать длительность цикла';
    } else if (isNaN(numCycleLength) || numCycleLength <= 0) {
      newErrors.cycleLength = 'Должно быть положительным числом';
    } else if (numCycleLength < 21) {
      newErrors.cycleLength = 'Минимальная длительность цикла 21 день';
    } else if (numCycleLength > 45) {
      newErrors.cycleLength = 'Максимальная длительность цикла 45 дней';
    }

    if (!periodLength) {
      newErrors.periodLength = 'Необходимо указать длительность менструации';
    } else if (isNaN(numPeriodLength) || numPeriodLength <= 0) {
      newErrors.periodLength = 'Должно быть положительным числом';
    } else if (numPeriodLength < 1) {
      newErrors.periodLength = 'Минимальная длительность 1 день';
    } else if (numPeriodLength > 10) {
      newErrors.periodLength = 'Максимальная длительность 10 дней';
    } else if (numPeriodLength >= numCycleLength) {
      newErrors.periodLength = 'Длительность менструации не может быть больше или равна длительности цикла';
    }
    
    if (!lutealPhaseLength) {
      newErrors.lutealPhaseLength = 'Необходимо указать длительность лютеиновой фазы';
    } else if (isNaN(numLutealPhaseLength) || numLutealPhaseLength <= 0) {
      newErrors.lutealPhaseLength = 'Должно быть положительным числом';
    } else if (numLutealPhaseLength < 10) {
      newErrors.lutealPhaseLength = 'Минимальная длительность 10 дней';
    } else if (numLutealPhaseLength > 16) {
      newErrors.lutealPhaseLength = 'Максимальная длительность 16 дней';
    } else if (numLutealPhaseLength >= numCycleLength) {
      newErrors.lutealPhaseLength = 'Длительность лютеиновой фазы не может быть больше или равна длительности цикла';
    }

    // Валидация дополнительных параметров
    if (includeSymptoms) {
      if (includeBbt) {
        if (!bbtRise) {
          newErrors.bbtRise = 'Необходимо указать повышение температуры';
        } else if (isNaN(numBbtRise) || numBbtRise <= 0) {
          newErrors.bbtRise = 'Должно быть положительным числом';
        } else if (numBbtRise < 0.2) {
          newErrors.bbtRise = 'Минимальное повышение 0.2°C';
        } else if (numBbtRise > 1) {
          newErrors.bbtRise = 'Максимальное повышение 1°C';
        }
      }

      if (ovulationTestDate) {
        if (!isValidDate(ovulationTestDate)) {
          newErrors.ovulationTestDate = 'Некорректная дата';
        } else {
          const ovulationTestDateObj = new Date(ovulationTestDate);
          const lastPeriodDateObj = new Date(lastPeriodDate);
          const currentDate = new Date();
          
          // Проверяем, что дата не в будущем
          if (ovulationTestDateObj > currentDate) {
            newErrors.ovulationTestDate = 'Дата не может быть в будущем';
          }
          
          // Проверяем, что дата не раньше последней менструации
          if (ovulationTestDateObj < lastPeriodDateObj) {
            newErrors.ovulationTestDate = 'Дата не может быть раньше даты последней менструации';
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета овуляции и фертильных дней
  const calculateOvulation = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCalendarData([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Расчет овуляции и фертильного окна
        const {
          ovulationDate,
          fertileStartDate,
          fertileEndDate,
          peakFertileStartDate,
          peakFertileEndDate,
          nextPeriodDate,
          periodEndDate
        } = calculateFertilityWindow(
          formData.lastPeriodDate,
          parseInt(formData.cycleLength, 10),
          parseInt(formData.lutealPhaseLength, 10)
        );

        // Корректировка с учетом дополнительных симптомов и тестов
        let adjustedOvulationDate = new Date(ovulationDate);
        let ovulationDateAdjusted = false;

        if (formData.includeSymptoms && formData.ovulationTestDate) {
          const testDate = new Date(formData.ovulationTestDate);
          // Если тест положительный, овуляция обычно наступает в течение 24-48 часов
          adjustedOvulationDate = new Date(testDate);
          adjustedOvulationDate.setDate(testDate.getDate() + 1);
          ovulationDateAdjusted = true;
        }

        // Коррекция фертильного окна, если овуляция была скорректирована
        let adjustedFertileStartDate = fertileStartDate;
        let adjustedFertileEndDate = fertileEndDate;
        let adjustedPeakFertileStartDate = peakFertileStartDate;
        let adjustedPeakFertileEndDate = peakFertileEndDate;

        if (ovulationDateAdjusted) {
          adjustedFertileStartDate = new Date(adjustedOvulationDate);
          adjustedFertileStartDate.setDate(adjustedOvulationDate.getDate() - 5);
          
          adjustedFertileEndDate = new Date(adjustedOvulationDate);
          adjustedFertileEndDate.setDate(adjustedOvulationDate.getDate() + 1);
          
          adjustedPeakFertileStartDate = new Date(adjustedOvulationDate);
          adjustedPeakFertileStartDate.setDate(adjustedOvulationDate.getDate() - 1);
          
          adjustedPeakFertileEndDate = new Date(adjustedOvulationDate);
        }

        const calculatedResults = {
          ovulationDate: adjustedOvulationDate,
          fertileStartDate: adjustedFertileStartDate,
          fertileEndDate: adjustedFertileEndDate,
          peakFertileStartDate: adjustedPeakFertileStartDate,
          peakFertileEndDate: adjustedPeakFertileEndDate,
          nextPeriodDate,
          periodEndDate,
          cycleLength: parseInt(formData.cycleLength, 10),
          periodLength: parseInt(formData.periodLength, 10),
          lutealPhaseLength: parseInt(formData.lutealPhaseLength, 10),
          ovulationDateAdjusted,
          calculatedAt: new Date()
        };

        setResults(calculatedResults);
        generateCalendarData(calculatedResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setCalendarData([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCalendarData, setIsCalculating, calculateFertilityWindow, generateCalendarData]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      lastPeriodDate: new Date().toISOString().split('T')[0],
      cycleLength: 28,
      periodLength: 5,
      lutealPhaseLength: 14,
      includeSymptoms: false,
      includeBbt: false,
      bbtRise: 0.4,
      includeCervicalMucus: false,
      ovulationTestDate: '',
      plannedPregnancy: true
    });
    setResults(null);
    setCalendarData([]);
    setErrors({});
    setSaveCycleName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения цикла
  const handleSaveCycle = () => {
    if (showSaveDialog) {
      const savedResult = saveCycle(saveCycleName || null);
      if (savedResult) {
        alert(`Цикл "${savedResult.notes}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCycleName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateOvulation();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-6 h-6 text-rose-500" />
          Параметры менструального цикла
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры цикла */}
        <CycleParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          cycleTypes={cycleTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Симптомы */}
        {formData.includeSymptoms && (
          <SymptomsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            ovulationSymptoms={ovulationSymptoms}
          />
        )}

        {/* Диалог сохранения цикла */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCycleName}
                onChange={(e) => setSaveCycleName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCycle}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Heart className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать овуляцию'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCycle}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить цикл
            </Button>
          )}
          {!formData.plannedPregnancy && (
            <Button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, plannedPregnancy: true }))}
              icon={<Book className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Режим планирования беременности
            </Button>
          )}
          {formData.plannedPregnancy && (
            <Button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, plannedPregnancy: false }))}
              icon={<Book className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Режим контрацепции
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;